/**
 * Design tokens — the single source of truth.
 *
 * Every value here is taken verbatim from design/README.md ("Hot Shot Debut"). CSS custom
 * properties are GENERATED from this file by generate-css.ts, so a colour can never drift
 * between the stylesheet and the code.
 *
 * design/DESIGN-SPEC.md §2 defines a different, superseded palette. If you are reading a hex
 * that is not below, it is from the old identity — see docs/DESIGN-CONFLICTS.md.
 */
/** The six neutrals plus two spot inks. Artwork is the loudest thing on any screen. */
export const color = {
    /** Application ground, content surfaces, strip fill on dark wells. */
    newsprint: "#F2F1EF",
    /** Top bars, headers, hover, unrated row background. */
    band: "#E7E5E0",
    /** Hairlines, borders, dividers. */
    rule: "#C9C7C1",
    /** Text, committed strip fills, inverted selection. */
    ink: "#1A1815",
    /**
     * Secondary text, labels, metadata.
     *
     * Was #6E6B64, which is 4.71 on newsprint but **4.22 on band** — and nearly every place it
     * carries small type sits on band: the top bar, the rate legend, an unrated row, the album's
     * track numbers. docs/DESIGN-CONFLICTS.md §F called it "marginal, acceptable at 15px+"; the axe
     * pass found it at 10px. This is the same hue two steps darker, clearing AA on both grounds
     * (5.09 / 4.56). One secondary colour that works on every ground beats two with a rule about
     * which ground you are on. Signed off; see §F.4.
     */
    grey: "#68665F",
    /** The dark surround behind every list, table, and score block. */
    well: "#1F1D18",
    /** Spot A — live strip fill, cursor band, focus ring, upward rank movement, ordered-tie edge. */
    vermilion: "#E5372B",
    /** Spot B — unrated / needs-review ONLY. Nothing else, ever. */
    flash: "#F2C230",
};
/** Supporting values that appear in the design files. */
export const support = {
    /** Empty strip cell on newsprint. */
    emptyCell: "#D6D4CE",
    /** Empty strip cell at input size; empty bar track. */
    emptyCellInput: "#E0DED8",
    /** Hairline / track inside a dark well. */
    ruleOnWell: "#3A362E",
    /** Secondary text ON the dark well. */
    greyOnWell: "#8C887E",
    /** Repeated rank numeral (tie group). */
    rankRepeat: "#B6B4AE",
    /**
     * Unrated score text on newsprint and band (`—`, the unmapped `MAP TO` label).
     * Was #8A6E32, which measured 4.27:1 on newsprint and 3.83:1 on band — below AA where it is
     * actually used. This clears both (5.27 / 4.72). Signed off; see docs/DESIGN-CONFLICTS.md §F.
     */
    unratedGold: "#7A6029",
    /** Error text. */
    error: "#A8442F",
    /** Body copy, two darkness levels. */
    body: "#4A4842",
    bodyDark: "#3A3833",
    /** Disabled / placeholder mono. */
    disabled: "#A5A29B",
    /** Drag grip. */
    grip: "#C1BEB7",
    /**
     * Vermilion at text weight, for the rare places the spot ink has to carry small type — the
     * list-builder operators. `vermilion` itself is 3.79:1: correct for a focus ring or a 5px band,
     * below AA as 11px text. This is 5.10:1 and reads as the same ink.
     *
     * It is also the FILL for any vermilion button carrying words: newsprint on `vermilion` is
     * 3.79 and on this is 5.10. The spot ink stays a UI colour; where it has to be read through,
     * it is this one. §F.5.
     */
    vermilionText: "#C22A1F",
    /* These three appear only in Cross-5a-Chart-Shelf.dc.html, not in the handoff's token table. */
    /** Empty display-strip cell on a cursored (solid ink) row. */
    emptyCellOnInk: "#4A463E",
    /**
     * Typographic artwork fallback at thumbnail size, on a `rule` square.
     * Was #5C5A54 — 4.08 on `rule`, and this is 7px type. #55534D clears AA (4.55) and reads as the
     * same grey. §F.4.
     */
    thumbFallback: "#55534D",
    /** Song title on an unrated (band) row — a touch lighter than ink. */
    unratedTitle: "#46443E",
    /**
     * The cursored row in an album track grid. Brighter than `newsprint`, so the row the cursor is
     * on lifts off the page while its vermilion head band says why. From the rate-loop prototype
     * (`bg: isCur ? '#FFFFFF' : PAPER`); not in the handoff's token table.
     */
    cursorRow: "#FFFFFF",
};
/**
 * Four roles, four widths. Self-hosted via @fontsource — no Google Fonts request.
 * Display is Anton only; it never sets body copy.
 */
export const font = {
    /** Rank numerals, page titles, scores, wordmark. Never body. */
    display: "Anton, Impact, sans-serif",
    /** Nav items, section labels, eyebrows, tabs, album titles in tiles. */
    spine: "'Barlow Condensed', 'Arial Narrow', sans-serif",
    /** Song titles, artist names, descriptions, controls. */
    body: "Barlow, system-ui, sans-serif",
    /** Every number without exception, plus keyboard hints and system labels. */
    data: "'IBM Plex Mono', ui-monospace, monospace",
};
/**
 * Geometry. Zero radius everywhere, no shadows anywhere — no exceptions, not on buttons,
 * inputs, chips, cards, wells, or artwork.
 */
export const geometry = {
    radius: "0",
    shadow: "none",
    /** 2px solid vermilion, 2px offset, applied instantly. A weak focus ring is a broken app. */
    focusRing: `2px solid ${color.vermilion}`,
    focusOffset: "2px",
    railWidth: "200px",
    topBarHeight: "44px",
    /**
     * One row height and one thumbnail size across every dense table. Three different heights
     * across three tables was a design defect repeated three times, not nuance — the next table
     * inherits these rather than inventing a fourth. See docs/DESIGN-CONFLICTS.md §I2.
     */
    rowHeight: "32px",
    rowArt: "24px",
    rowGap: "2px",
};
/**
 * The one breakpoint in the app.
 *
 * design/README.md §13 defines mobile as *below 900px*, and there is exactly one line between the
 * two designs — no tablet tier, no per-component threshold. It lives here because both the
 * stylesheets and the JavaScript need it, and a second copy of a number that decides which of two
 * designs you are looking at is the kind that drifts silently.
 *
 * CSS cannot read a custom property inside a media query, so the stylesheets restate it literally.
 * `tokens.audit.test.ts` fails the build on any other breakpoint reaching the bundle.
 */
export const MOBILE_MAX_WIDTH = 899;
export const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
/**
 * The rating strip — the one component that carries every score in the app.
 *
 * Three sizes, one language. There is no second visual language for scores anywhere: no stars,
 * no donuts, no rings, no gradient bars. Sizes are per design/README.md; the `specimen` preset
 * is the slightly smaller rendering used on the identity sheet.
 */
export const strip = {
    input: { cell: 34, gap: 4, height: 64, numeral: 11 },
    /** Narrow panes (album page, side columns). */
    inputPane: { cell: 30, gap: 4, height: 58, numeral: 11 },
    /** Mobile: two rows of five, numerals bottom-aligned inside each cell. */
    inputMobile: { cell: 62, gap: 8, height: 56, numeral: 11 },
    /** Every table row, card and list. */
    display: { cell: 4, gap: 2, height: 14, numeral: 0 },
    /** Cross-5a-Chart-Shelf.dc.html specimen dimensions. */
    specimen: { cell: 28, gap: 4, height: 50, numeral: 10 },
    /** Inside a dark well, at the identity sheet's size. */
    well: { cell: 20, gap: 3, height: 36, numeral: 0 },
};
/**
 * Type scale. Every size design/README.md enumerates, by role. Anton carries display numerals
 * and page titles and never sets body; Plex Mono carries every number inside a table and is
 * always tabular-nums.
 */
export const type = {
    display: {
        heroCount: 82,
        artistScore: 72,
        section: 62,
        albumScore: 56,
        rateCard: 48,
        pageTitle: 44,
        statScore: 40,
        screenTitle: 36,
        rateSongTitle: 34,
        rank36: 30,
        rank30: 26,
        rank28: 24,
    },
    spine: {
        dashboardSection: 30,
        panelTitle: 22,
        tableSection: 20,
        settingsSection: 19,
        decade: 18,
        nav: 15,
        railLabel: 12,
        tileTitle: 11,
    },
    body: {
        rateArtist: 17,
        intro: 16,
        list: 15,
        row: 14,
        dense: 13.5,
        secondary: 13,
        artistColumn: 12.5,
    },
    data: {
        specimen: 15,
        tableScore: 14,
        rowScore: 13,
        year: 12,
        hint: 11,
        eyebrow: 10,
        caption: 9.5,
    },
    /** All-caps mono at 10px with wide tracking is the eyebrow voice on nearly every surface. */
    tracking: {
        eyebrow: "0.12em",
        eyebrowWide: "0.18em",
        nav: "0.1em",
        settings: "0.12em",
        tableSection: "0.08em",
    },
};
/** Base 4. These are the values the design files actually use — not a generated ramp. */
export const space = [2, 3, 4, 5, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 32, 40, 44, 48, 56, 64];
export const tokens = { color, support, font, geometry, strip, type, space };
//# sourceMappingURL=index.js.map